/**
 * Carousel
 * ======================
 * Lightweight horizontal carousel built on native scrolling, so it degrades to
 * a plain scrollable row without JavaScript. Mark up as:
 *
 *   <div data-carousel data-carousel-autoplay="5000">
 *     <button data-carousel-prev></button>
 *     <div data-carousel-track>…items…</div>
 *     <button data-carousel-next></button>
 *   </div>
 *
 * data-carousel-autoplay is optional: a delay in milliseconds between moves.
 * data-carousel-duration is optional: how long each move takes, in milliseconds.
 */
class Carousel {
  /**
   * @param {HTMLElement} root Element wrapping the track and its controls.
   * @param {Object} [options]
   * @param {string} [options.disabledClass] Class added to a control that can no longer scroll.
   */
  constructor(root, { disabledClass = "is-disabled" } = {}) {
    this.root = root;
    this.disabledClass = disabledClass;

    this.track = null;
    this.prev = null;
    this.next = null;

    this.timer = null;
    this.delay = 0;
    this.paused = false;

    this.duration = 900;
    this.frame = null;
    this.target = 0;

    this._onScroll = this._onScroll.bind(this);
  }

  /**
   * Wire up the controls and keep their state in sync with the track.
   */
  init() {
    if (!this.root) return;

    this.track = this.root.querySelector("[data-carousel-track]");
    if (!this.track) return;

    const duration = parseInt(this.root.dataset.carouselDuration, 10);
    if (duration >= 0) this.duration = duration;

    this.prev = this.root.querySelector("[data-carousel-prev]");
    this.next = this.root.querySelector("[data-carousel-next]");

    if (this.prev) this.prev.addEventListener("click", () => this._manual(-1));
    if (this.next) this.next.addEventListener("click", () => this._manual(1));

    this.track.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll);

    // A carousel inside a hidden tab panel measures 0 wide, so the controls
    // would settle on the wrong state. Re-check once it is actually laid out.
    if (typeof ResizeObserver === "function") {
      this.resizeObserver = new ResizeObserver(this._onScroll);
      this.resizeObserver.observe(this.track);
    }

    this._onScroll();

    const delay = parseInt(this.root.dataset.carouselAutoplay, 10);
    if (delay > 0) this.startAutoplay(delay);
  }

  /**
   * Advance one item on a timer, looping back to the start at the end.
   * Pauses while hovered or focused, and does nothing for users who prefer
   * reduced motion.
   * @param {number} delay Milliseconds between moves.
   */
  startAutoplay(delay) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.delay = delay;

    this.root.addEventListener("mouseenter", () => (this.paused = true));
    this.root.addEventListener("mouseleave", () => (this.paused = false));
    this.root.addEventListener("focusin", () => (this.paused = true));
    this.root.addEventListener("focusout", () => (this.paused = false));

    this.restartAutoplay();
  }

  /**
   * Reset the timer, so a manual move is not followed immediately by an
   * automatic one.
   */
  restartAutoplay() {
    if (!this.delay) return;

    clearInterval(this.timer);
    this.timer = setInterval(() => this.advance(), this.delay);
  }

  /**
   * Move forward one item, or back to the start once the end is reached.
   * Skipped while paused, off-screen or inside a hidden tab panel.
   */
  advance() {
    if (this.paused || document.hidden || !this.track.clientWidth) return;

    const max = this.track.scrollWidth - this.track.clientWidth;

    if (this.track.scrollLeft >= max - 1) {
      this.animateTo(0);
    } else {
      this.scroll(1);
    }
  }

  /**
   * Scroll the track by a single item.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scroll(direction) {
    // Mid-animation, step from where the track is heading rather than where it
    // happens to be, so a quick second click still lands on an item.
    const from = this.frame ? this.target : this.track.scrollLeft;
    this.animateTo(from + this.step() * direction);
  }

  /**
   * Ease the track to a scroll position over the configured duration.
   * Snapping and native smooth scrolling are suspended while it runs, since
   * both would fight a position set on every frame.
   * @param {number} position Target scrollLeft in pixels.
   */
  animateTo(position) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const end = Math.max(0, Math.min(position, max));
    const start = this.track.scrollLeft;
    const distance = end - start;

    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.target = end;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!distance || reduced || !this.duration) {
      this.track.scrollLeft = end;
      return;
    }

    this.track.style.scrollSnapType = "none";
    this.track.style.scrollBehavior = "auto";

    const begin = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - begin) / this.duration, 1);
      this.track.scrollLeft = start + distance * this._ease(progress);

      if (progress < 1) {
        this.frame = requestAnimationFrame(tick);
        return;
      }

      this.frame = null;
      this.track.style.scrollSnapType = "";
      this.track.style.scrollBehavior = "";
    };

    this.frame = requestAnimationFrame(tick);
  }

  /**
   * Ease-in-out cubic: a gentle start and a gentle stop.
   * @param {number} t Progress from 0 to 1.
   * @returns {number}
   */
  _ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /**
   * Width of one item plus the gap that follows it. Measured from the DOM so
   * the step stays correct however the track is sized at the current breakpoint.
   * @returns {number} Distance in pixels between consecutive items.
   */
  step() {
    const items = this.track.children;
    if (!items.length) return this.track.clientWidth;

    // Two items give the exact pitch, gap included.
    if (items.length > 1) {
      const pitch = items[1].getBoundingClientRect().left -
        items[0].getBoundingClientRect().left;
      if (pitch > 0) return pitch;
    }

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return items[0].getBoundingClientRect().width + gap;
  }

  /**
   * Handle a prev/next click, restarting the autoplay timer.
   * @param {number} direction -1 for previous, 1 for next.
   */
  _manual(direction) {
    this.scroll(direction);
    this.restartAutoplay();
  }

  /**
   * Disable whichever control has nothing left to scroll towards.
   */
  _onScroll() {
    // Skip while hidden: every measurement is 0 and would disable both controls.
    if (!this.track.clientWidth) return;

    const max = this.track.scrollWidth - this.track.clientWidth;
    const position = this.track.scrollLeft;

    if (this.prev) this.prev.classList.toggle(this.disabledClass, position <= 1);
    if (this.next) this.next.classList.toggle(this.disabledClass, position >= max - 1);
  }
}

/**
 * Boot every carousel on the page.
 */
const initCarousels = () => {
  document.querySelectorAll("[data-carousel]").forEach((root) => {
    new Carousel(root).init();
  });
};

export default initCarousels;
export { Carousel };
